import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { TrackedFace } from '../../types';

interface FaceDetectionResult {
  detections: Array<{ boundingBox?: { originX: number; originY: number; width: number; height: number }; categories?: Array<{ score?: number }> }>;
}

interface Detector { detectForVideo: (video: HTMLVideoElement, timestamp: number) => FaceDetectionResult; close: () => void }

export function useFaceTracking(videoRef: RefObject<HTMLVideoElement | null>, enabled: boolean, guestName: string) {
  const [faces, setFaces] = useState<TrackedFace[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'offline'>('loading');
  const detectorRef = useRef<Detector | null>(null);

  useEffect(() => {
    if (!enabled) {
      setFaces([]);
      setStatus('offline');
      return;
    }
    let cancelled = false;
    let timer = 0;
    const start = async () => {
      try {
        const { FaceDetector, FilesetResolver } = await import('@mediapipe/tasks-vision');
        const vision = await FilesetResolver.forVisionTasks('https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm');
        const detector = await FaceDetector.createFromOptions(vision, {
          baseOptions: { modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite', delegate: 'GPU' },
          runningMode: 'VIDEO',
          minDetectionConfidence: 0.55,
        });
        if (cancelled) { detector.close(); return; }
        detectorRef.current = detector as Detector;
        setStatus('ready');
        timer = window.setInterval(() => {
          const video = videoRef.current;
          if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
          const result = detectorRef.current?.detectForVideo(video, performance.now());
          const next = result?.detections.flatMap((detection, index) => {
            const box = detection.boundingBox;
            if (!box) return [];
            return [{
              id: `face-${index}`,
              bbox: { x: box.originX / video.videoWidth, y: box.originY / video.videoHeight, width: box.width / video.videoWidth, height: box.height / video.videoHeight },
              label: index === 0 ? 'LUN' : guestName,
              confidence: detection.categories?.[0]?.score,
              identityId: null,
            } satisfies TrackedFace];
          }) ?? [];
          setFaces(next);
        }, 90);
      } catch {
        setStatus('offline');
      }
    };
    void start();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      detectorRef.current?.close();
      detectorRef.current = null;
    };
  }, [enabled, guestName, videoRef]);

  return { faces, status };
}
